import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { closeTopModal, consumeSuppressFlag, nextGuardHash, hasNavigationApi, hasOpenModal } from "./utils/modalStack";
import { doc, onSnapshot } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { NotificationProvider } from "./contexts/NotificationContext";
import { BannerInquiryUnreadProvider } from "./contexts/BannerInquiryUnreadContext";
import NotificationToast from "./components/ui/NotificationToast";
import NotifPermissionPrompt from "./components/ui/NotifPermissionPrompt";
import InstallBanner from "./components/ui/InstallBanner";
import IntroVideo from "./components/layout/IntroVideo";
import { registerFcmToken, db as firestoreDb } from "./firebase";
import { isTokenValid, isRefreshValid, isLoggedIn as isLoggedInFn } from "./hooks/useAuth";
import { useForceLogoutWatcher } from "./hooks/useForceLogoutWatcher";
import { useAuthVersion } from "./hooks/useAuthVersion";
import { useBodyScrollLock } from "./hooks/useBodyScrollLock";
import HomePage from "./pages/HomePage"; // 홈은 즉시 로드(임계 경로)
// 나머지 라우트는 지연 로딩(코드 스플리팅) → 초기 번들에서 제외 → 첫 실행 단축
const AdvertiserDetailPage = lazy(() => import("./pages/AdvertiserDetailPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const OAuthCallbackPage = lazy(() => import("./pages/OAuthCallbackPage"));
const OAuthCompletePage = lazy(() => import("./pages/OAuthCompletePage"));
const OAuthErrorPage = lazy(() => import("./pages/OAuthErrorPage"));
const BannerDetailRoute = lazy(() => import("./pages/BannerDetailRoute"));
const BannerCommentsRoute = lazy(() => import("./pages/BannerCommentsRoute"));

// 글로벌 fetch 인터셉터: 401 시 refresh token으로 자동 갱신
const API_BASE = process.env.REACT_APP_API_URL || "https://api.trippang.com";
const _originalFetch = window.fetch;

// "success" = 갱신 성공 / "invalid" = 서버가 토큰을 실제로 거부함(진짜 로그아웃 사유) /
// "network" = 요청 자체가 서버에 도달 못함(배포 재시작 등 일시적 문제 — 로그아웃 사유 아님)
type RefreshOutcome = "success" | "invalid" | "network";
let _refreshing: Promise<RefreshOutcome> | null = null;

async function _attemptRefresh(rt: string): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await _originalFetch(`${API_BASE}/user/session/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
  } catch {
    // fetch 자체 실패(네트워크 단절/서버 재시작 타이밍 등) — 토큰 유효성과 무관
    return "network";
  }
  let data: { status?: string; data?: { access_token?: string; refresh_token?: string } };
  try {
    data = await res.json();
  } catch {
    return "network";
  }
  if (data.status === "success" && data.data?.access_token) {
    localStorage.setItem("access_token", data.data.access_token);
    // refresh token rotation — 백엔드가 새 refresh 발급 시 같이 저장
    // (이전 refresh 는 백엔드에서 revoke 됨, 다음 호출에 새 토큰 사용 필수)
    if (data.data.refresh_token) {
      localStorage.setItem("refresh_token", data.data.refresh_token);
    }
    // Header 등 로그인 상태 의존 컴포넌트 즉시 동기화
    window.dispatchEvent(new CustomEvent("auth-changed"));
    return "success";
  }
  return "invalid";
}

async function _tryRefresh(): Promise<RefreshOutcome> {
  const rt = localStorage.getItem("refresh_token");
  if (!rt) return "invalid";
  const first = await _attemptRefresh(rt);
  if (first !== "invalid") return first; // success 또는 network — 재시도해도 의미 없음

  // 자동로그인 정책: refresh token은 1회용 회전(rotation)이라, 탭을 여러 개 띄워두면
  // 한 탭이 먼저 갱신에 성공하는 순간 다른 탭이 들고 있던 (이제는 낡은) refresh_token은
  // 실패한다. 이걸 바로 로그아웃 처리하면 방금 다른 탭이 정상 갱신해둔 세션까지
  // localStorage에서 지워버리게 되므로, invalid(진짜 거부) 시에만 localStorage의 최신
  // refresh_token으로 1회 더 재시도한다(다른 탭이 이미 갱신해뒀다면 여기서 성공).
  const latest = localStorage.getItem("refresh_token");
  if (latest && latest !== rt) {
    return _attemptRefresh(latest);
  }
  return "invalid";
}

// 서버 세션은 살아있는데 클라이언트만 로컬 로그아웃 처리하는 경우(감사 페이지 조회용) —
// 실패해도 무시(best-effort), 로그아웃 자체를 막지 않음
function _reportClientLogout(reason: string) {
  const userId = localStorage.getItem("user_id");
  _originalFetch(`${API_BASE}/user/session/client-logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId ? Number(userId) : null, reason }),
  }).catch(() => {});
}

function _clearAuth(reason: string) {
  _reportClientLogout(reason);
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_id");
  localStorage.removeItem("uid");
  localStorage.removeItem("nick_name");
  // reload 대신 auth-changed → Header 등 로그인 상태 즉시 갱신 (자동 로그아웃 시 화면 튕김 제거)
  window.dispatchEvent(new CustomEvent("auth-changed"));
}

// 빌드 버전 체크 (API 응답 헤더 기반)
let _buildVersion = "";
let _updateDetected = false;
const _onUpdateDetected: (() => void)[] = [];

// fetch 인터셉터: 우리 API만 처리, 외부(Firestore/reCAPTCHA/FCM)는 원본 fetch 직접 사용
window.fetch = function(...args: Parameters<typeof fetch>): Promise<Response> {
  const url = typeof args[0] === "string" ? args[0] : args[0] instanceof Request ? args[0].url : "";
  const isOurApi = url.includes("api.trippang.com") || url.includes("localhost:8000");
  // 외부 서비스 → 원본 fetch 직접 반환 (async 래핑 없음)
  if (!isOurApi) return _originalFetch.apply(window, args);

  // 우리 API → 401 인터셉트
  return _originalFetch.apply(window, args).then(async (res) => {
    if (res.status === 401 && localStorage.getItem("access_token")) {
      const [, init] = args as [RequestInfo, RequestInit | undefined];
      if (!_refreshing) _refreshing = _tryRefresh().finally(() => { _refreshing = null; });
      const outcome = await _refreshing;
      if (outcome === "success") {
        const newHeaders = new Headers(init?.headers || {});
        newHeaders.set("Authorization", `Bearer ${localStorage.getItem("access_token")}`);
        return _originalFetch(args[0], { ...init, headers: newHeaders });
      }
      // 서버가 실제로 거부했을 때만 로그아웃 — 네트워크 문제(network)는 이번 요청만 실패 처리
      if (outcome === "invalid") {
        _clearAuth("REFRESH_FAILED");
      }
    }
    // 빌드 버전 체크
    if (!_updateDetected) {
      const v = res.headers.get("X-Build-Version");
      if (v) {
        if (!_buildVersion) _buildVersion = v;
        else if (_buildVersion !== v) {
          _updateDetected = true;
          _onUpdateDetected.forEach((cb) => cb());
        }
      }
    }

    return res;
  }) as Promise<Response>;
};

export async function _forceRefresh() {
  // 1. 서비스워커 해제
  if ("serviceWorker" in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
  // 2. 캐시 삭제
  if ("caches" in window) {
    const names = await caches.keys();
    await Promise.all(names.map((n) => caches.delete(n)));
  }
  // 3. 강제 리로드 (intro-shown 은 그대로 유지 — 로고/업데이트 새로고침 후 인트로 재생 안 함)
  window.location.reload();
}

// 렌더 전 정리: refresh도 만료된 경우에만 전체 정리 (access만 만료면 refresh로 자동 갱신 가능하므로 보존)
if (localStorage.getItem("access_token") && !isTokenValid() && !isRefreshValid()) {
  _reportClientLogout("STARTUP_BOTH_EXPIRED");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user_id");
  localStorage.removeItem("uid");
  localStorage.removeItem("nick_name");
}

export default function App() {
  const { t } = useTranslation();
  useAuthVersion(); // auth-changed 시 App 재렌더 → isLoggedIn 재계산 → FCM 등 effect 재실행 (reload 대체)
  const isLoggedIn = isLoggedInFn();
  useForceLogoutWatcher();
  const [serviceBlocked, setServiceBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  // 새 버전 감지 (API 응답 헤더 X-Build-Version 비교)
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // 강력 새로고침(_forceRefresh) 은 SW 해제+캐시 삭제까지 마친 뒤에야 reload 되므로
  // 버튼 클릭 직후 잠깐 아무 반응이 없어 보임 — 스피너로 진행 중임을 표시.
  const [hardRefreshing, setHardRefreshing] = useState(false);
  const startHardRefresh = () => { setHardRefreshing(true); _forceRefresh(); };
  useEffect(() => {
    const cb = () => setUpdateAvailable(true);
    _onUpdateDetected.push(cb);
    return () => { const i = _onUpdateDetected.indexOf(cb); if (i >= 0) _onUpdateDetected.splice(i, 1); };
  }, []);
  const updateModalRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(updateModalRef, updateAvailable);
  const serviceBlockedRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(serviceBlockedRef, serviceBlocked);

  // SW 자동 갱신 확인 — 마운트 시 + 탭 visible 전환 시 SW.update() 호출.
  // #479: 이전엔 controllerchange(새 SW 로 교체) 시 window.location.reload() 를 강제
  // 호출했는데, 이게 인트로 영상 재생 중 발화하면 영상 종료 직후 reload 되면서 부팅
  // 로고 화면이 한 번 더 보이는 부작용이 있었다(배포 직후 접속한 사용자가 "로고가 두 번
  // 나온다"고 신고). 새 버전 안내는 X-Build-Version 응답 헤더 비교로 뜨는
  // updateAvailable 모달(사용자가 직접 새로고침 버튼을 누름) 하나로 충분하므로, 이
  // 강제 reload 로직은 제거 — SW 갱신 확인만 남긴다.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const tryUpdate = () => {
      navigator.serviceWorker.getRegistration().then((reg) => reg?.update()).catch(() => {});
    };
    tryUpdate();
    const onVis = () => { if (document.visibilityState === "visible") tryUpdate(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // 서비스 차단 상태 실시간 구독 — 첫 페인트 후로 지연(초기 로딩 시 Firestore long-polling 연결 경합 제거).
  //  차단은 드물고, 1초 지연돼도 감지엔 문제없음 → 첫 화면 로딩 속도 우선.
  useEffect(() => {
    let unsub = () => {};
    const start = () => {
      unsub = onSnapshot(
        doc(firestoreDb, "service_status", "current"),
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setServiceBlocked(!!data.blocked);
            setBlockReason(data.reason || "");
          } else {
            setServiceBlocked(false);
          }
        },
        () => setServiceBlocked(false)
      );
    };
    const ric = (window as any).requestIdleCallback;
    const id = ric ? ric(start, { timeout: 2000 }) : window.setTimeout(start, 1200);
    return () => {
      if (ric && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(id);
      else window.clearTimeout(id);
      unsub();
    };
  }, []);


  // 앱 로드 시 FCM 토큰 자동 갱신
  useEffect(() => {
    if (!isLoggedIn) return;
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      registerFcmToken();
    }
  }, [isLoggedIn]);

  // 요구사항 #296 가드는 BackGuard 컴포넌트가 BrowserRouter 내부에서 useLocation 으로 처리

  return (
    <NotificationProvider>
    <BannerInquiryUnreadProvider>
    <BrowserRouter>
      <BackGuard />
      <div className="pwa-titlebar" aria-hidden="true" />
      <IntroVideo />
      <NotifPermissionPrompt />
      <NotificationToast />
      <InstallBanner />

      {/* 업데이트 강제 새로고침 모달 — 닫기 없음 */}
      {updateAvailable && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center" onKeyDown={(e) => { if (e.key === "Enter") startHardRefresh(); }} tabIndex={0} ref={(el) => { (updateModalRef as React.MutableRefObject<HTMLDivElement | null>).current = el; el?.focus(); }}>
          <div className="w-full max-w-sm rounded-2xl border border-amber-500/30 bg-slate-900 p-8 text-center space-y-5">
            <h2 className="text-lg font-bold text-white">TRIPPANG</h2>
            <p className="text-sm text-amber-400">{t("common.service_updated")}</p>
            <button
              onClick={startHardRefresh}
              disabled={hardRefreshing}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-8 py-2.5 text-sm font-bold text-black hover:bg-amber-400 transition-colors disabled:opacity-70"
            >
              {hardRefreshing ? t("common.refreshing") : t("common.refresh")}
            </button>
          </div>
        </div>
      )}
      {hardRefreshing && createPortal(
        // 화면 전체 오버레이 스피너 — Header.tsx 로고 강력 새로고침과 동일 패턴/위치로 통일.
        <div
          className="fixed top-0 left-0 z-[9999] flex items-center justify-center bg-slate-950/80"
          style={{ width: "100vw", height: "100dvh" }}
        >
          <div className="h-10 w-10 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
        </div>,
        document.body
      )}

      {/* 서비스 차단 모달 — 닫기 버튼 없음 */}
      {serviceBlocked && (
        <div ref={serviceBlockedRef} className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-slate-900 p-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
                <svg className="h-8 w-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">TRIPPANG</h2>
              <p className="mt-2 text-sm text-red-400 font-medium">{t("common.service_unavailable")}</p>
            </div>
            {blockReason && (
              <div className="rounded-xl bg-red-500/10 border border-red-500/20 px-4 py-3">
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{blockReason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <AppRoutes />
      </Suspense>
    </BrowserRouter>
    </BannerInquiryUnreadProvider>
    </NotificationProvider>
  );
}

/**
 * AppRoutes — #449 3차 대응: 베너 상세를 팝업(모달)이 아니라 "background location"
 * 패턴의 진짜 라우트(/banner/:bannerId)로 분리. 열고 닫는 게 진짜 페이지 전환(push/pop)이라
 * BackGuard/PopupShell이 히스토리를 대신 관리해줄 필요가 없어지고, 안드로이드 시스템
 * 뒤로가기도 react-router가 원래 하던 대로 정상 동작한다(destPath !== curPath 케이스로
 * BackGuard가 이미 통과시켜주는 경로).
 * - backgroundLocation 이 있으면(=인앱 클릭으로 진입) 메인 Routes는 그 배경 위치를 그대로
 *   유지하고, 오버레이 Routes가 /banner/:bannerId 를 그 위에 얹는다.
 * - backgroundLocation 이 없으면(=공유 링크로 직접 진입) 메인 Routes도 실제 위치
 *   (/banner/:bannerId)를 그대로 써서 HomePage가 매칭되고, 오버레이가 그 위에 얹힌다 —
 *   결과적으로 항상 "홈 + 오버레이" 형태로 보인다.
 */
function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation;

  return (
    <>
      <Routes location={backgroundLocation || location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/banner/:bannerId" element={<HomePage />} />
        <Route path="/banner/:bannerId/comments" element={<HomePage />} />
        <Route path="/advertiser/:advertiserKey" element={<AdvertiserDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
        <Route path="/oauth/complete" element={<OAuthCompletePage />} />
        <Route path="/oauth/error" element={<OAuthErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {location.pathname.startsWith("/banner/") && (
        <Routes>
          <Route path="/banner/:bannerId" element={<BannerDetailRoute />} />
          <Route path="/banner/:bannerId/comments" element={<BannerCommentsRoute />} />
        </Routes>
      )}
    </>
  );
}

const EXIT_SENTINEL = "__trippang_exit_sentinel__";
// 4번째 사용자 신고(2026.07.17) 원인 진단: 토스트 노출시간(TOAST_DURATION_MS)과 재확인
// 대기시간(EXIT_ARM_WINDOW_MS)이 완전히 같으면 두 setTimeout의 실행 순서가 매번 브라우저
// 스케줄링에 따라 갈려서, 토스트가 떠 있는데도 두 번째 누름이 씹히는 경우가 생김 — 재확인
// 대기시간은 항상 토스트 노출시간보다 살짝 더 길게 유지해야 함(완전히 같게 두지 말 것).
// 2026.07.24: 정책상 종료를 의도적으로 어렵게 만들기 위해 두 값 모두 짧게 재조정했었음
// (토스트 0.8초 / 재확인 1초).
// 2026.07.25: 토스트를 탭하면 확정종료되는 기능(tapConfirmExit)이 추가되면서, 토스트가
// 너무 짧으면 알아보고 탭하기도 전에 사라져버림 — 토스트 노출시간을 3초로 늘림. 재확인
// 대기시간은 여전히 노출시간보다 살짝 더 길게(0.2초 여유) 유지.
const TOAST_DURATION_MS = 3000;
const EXIT_ARM_WINDOW_MS = 3200;

/**
 * BackGuard — 요구사항 #296 / #449 재작성
 * Android 모바일 뒤로가기 가드.
 * - 모달 열려있으면 → 최상위 모달 닫고 가드 유지
 * - 비-홈 페이지 (예: /login) → 라우터 정상 백 통과, 가드 재푸시
 * - 홈("/") 에서 뒤로가기 → 토스트 "한 번 더 누르면 종료" + 가드 재푸시
 * - 토스트 후 EXIT_ARM_WINDOW_MS(3초) 이내 또 뒤로가기 → 종료 시도 (실제 브라우저 back 통과)
 *   (2026.07.17: 이 대기시간이 토스트 노출시간과 같은 값이라 겹쳐서 재확인이 씹히던 버그 수정 — 아래 상수 참고)
 *
 * #449: 원래 raw window.history.pushState/popstate 로 구현했는데, BrowserRouter 를
 * 쓰는 상태에서 브라우저 히스토리 API를 직접 건드리면 react-router 내부의 히스토리 추적과
 * 어긋나 뒤로가기 depth 가 꼬이는 현상(리액트라우터 공식 문서/이슈에서도 알려진 문제)이
 * 있었음 — 베너 상세를 거쳐 나온 뒤 종료 확인 토스트가 안 뜨거나 페이지가 그냥 꺼져버리던
 * 원인. useNavigate/useNavigationType(react-router 자체 API)로 전부 교체해서
 * react-router 의 내부 히스토리 추적과 항상 일치하도록 함.
 *
 * #449 (2차): 위 history sentinel 방식도, 최신 크롬(안드로이드)의 예측 뒤로가기
 * (predictive back gesture)가 같은 문서 안의 entry를 popstate 없이 여러 개 건너뛰어
 * 버리는 문제로 다시 깨졌음(해시로 entry를 구분해도 동일하게 재현됨 — 문서 자체가
 * 동일해서 뭉뚱그려지는 것으로 보임). window.navigation(Navigation API)이 있으면
 * 그 쪽 event.destination.sameDocument로 "진짜 문서 이탈"만 정확히 판별해 완전히
 * 대체하고, 없는 구형 브라우저에서만 아래 sentinel 방식을 그대로 유지(폴백).
 *
 * 2026.07.24: popstate 사후대응 방식(activation 문제 회피 목적)으로 전면 교체를
 * 시도했다가 실기기 테스트에서 상황이 더 악화되어(#449 2차의 예측제스처 스킵 문제가
 * 다시 재현된 것으로 추정) 즉시 원복. Navigation API 방식이 현재 기준 상태 유지.
 */
function BackGuard() {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const prevPathRef = useRef<string>(location.pathname);

  const exitArmedRef = useRef(false);
  const armedTimerRef = useRef<number | undefined>(undefined);
  const toastElRef = useRef<HTMLDivElement | null>(null);

  // #535 (2026.07.25): 뒤로가기를 연속으로 누르면(간격이 EXIT_ARM_WINDOW_MS를 넘는 경우)
  // 각 누름이 독립적으로 "재확인"을 다시 시도하는데, 그 시점에 uaIsActive 가 이미 소진돼
  // cancelable:false 로 판정되면 우리 코드는 "다시 토스트"를 의도해도 실제 뒤로가기 자체는
  // 못 막아서 그냥 종료돼버리는 경우가 있음(반복 누름 시 activation 예산이 소진되는 것으로
  // 추정, 실측으로 재현 확인). onTap 이 있으면 토스트 자체를 탭 가능하게 만들어서, 진짜
  // 클릭(activation 문제 없음)으로 확정 종료를 대체 경로로 제공 — 뒤로가기 두 번은 여전히
  // 타이밍에 따라 성공/실패하지만(의도된 "종료 어렵게" 정책 유지), 탭은 항상 확정적으로 동작.
  const showToast = (msg: string, onTap?: () => void) => {
    if (toastElRef.current) toastElRef.current.remove();
    const el = document.createElement("div");
    el.textContent = msg;
    el.style.cssText = `position:fixed;left:50%;bottom:24%;transform:translateX(-50%);z-index:99999;background:rgba(15,23,42,0.92);color:#fff;padding:10px 18px;border-radius:9999px;font-size:13px;font-weight:600;box-shadow:0 6px 20px rgba(0,0,0,0.3);${onTap ? "pointer-events:auto;cursor:pointer;" : "pointer-events:none;"}`;
    if (onTap) {
      el.addEventListener("click", () => { onTap(); }, { once: true });
    }
    document.body.appendChild(el);
    toastElRef.current = el;
    window.setTimeout(() => { if (el === toastElRef.current) { el.remove(); toastElRef.current = null; } }, TOAST_DURATION_MS);
  };

  // 토스트 탭 확정종료 — window.history.length 전체만큼 되돌려서, 우리가 그동안 쌓아온
  // sentinel/재앵커 entry 가 몇 겹이든 상관없이 한 번에 진짜 이전 상태(또는 앱 종료)까지 도달.
  const tapConfirmExit = () => {
    if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
    exitArmedRef.current = false;
    try { navigate(-window.history.length); } catch { /* */ }
  };

  // #449 재접속 시 토스트 미발동 진단용 임시 — /public/client-errors(기존 오류리포트 endpoint)로
  // 이벤트를 보내 audit.exception_logs 에서 조회. sid 는 sessionStorage 기반이라, "재접속" 시
  // sid 가 그대로 유지되면(=진짜 새 로드가 아니라 백그라운드 재개) 가설이 맞다는 직접 증거가 됨.
  // 검증 끝나면 삭제.
  const dlog = (tag: string, data?: Record<string, unknown>) => {
    try {
      let sid = sessionStorage.getItem("__bg_dbg_sid__");
      if (!sid) { sid = Math.random().toString(36).slice(2, 10); sessionStorage.setItem("__bg_dbg_sid__", sid); }
      // 기기 로컬 발생시각(ms epoch) — 서버 수신시각과 분리해서, 백그라운드/네트워크 지연으로
      // 여러 로그가 한꺼번에 도착해도 실제 기기에서의 이벤트 간격을 정확히 계산 가능하게.
      const payload = JSON.stringify({
        message: `[BG] ${tag} t=${Date.now()} sid=${sid} ${data ? JSON.stringify(data) : ""}`.slice(0, 990),
        error_class: "BACKGUARD_DEBUG",
        kind: "backguard",
        app: "frontuser",
        page_url: window.location.href,
      });
      // 뒤로가기 순간 발생하는 로그(armToast/exit)는 페이지 전환/백그라운드 전환과 동시에
      // 발생해서 일반 fetch 는 브라우저가 중간에 취소해버려 서버에 도착 안 하는 경우가 있음
      // (실제로 토스트는 떴는데 armToast 로그가 하나도 안 남던 문제의 원인으로 추정) —
      // sendBeacon 은 페이지 이탈/백그라운드 전환 중에도 전송을 보장하도록 설계된 API라 교체.
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(`${API_BASE}/public/client-errors`, blob);
      } else {
        fetch(`${API_BASE}/public/client-errors`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
        }).catch(() => {});
      }
    } catch { /* */ }
  };
  // #496: "메인에서 배너 터치/스크롤 안 됨(특히 배너상세 진입 후 뒤로가기 시)" 안드로이드
  // PWA 신고 — body.overflow:hidden 이 PopupShell 의 scroll-lock에서 걸리는데, Android
  // 백버튼이 React 언마운트를 못 거치고(#449, navigate 이벤트 자체가 안 옴) bfcache로 그대로
  // 얼려서 복원하면 cleanup(overflow 원복)이 실행 안 된 채로 남을 수 있음 — pageshow/visible
  // 시점마다 "열린 모달이 없는데도 scroll-lock 남아있음" 이면 강제로 풀어주는 방어 로직.
  const forceUnlockScrollIfStuck = (tag: string) => {
    if (!hasOpenModal() && document.body.style.overflow === "hidden") {
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      document.documentElement.style.overscrollBehavior = "";
      dlog(`${tag}_forceUnlockScroll`);
    }
  };
  useEffect(() => {
    dlog("component_mount", { historyLength: window.history.length, ua: navigator.userAgent });
    forceUnlockScrollIfStuck("mount");
    const onVis = () => {
      dlog("visibilitychange", { state: document.visibilityState });
      if (document.visibilityState === "visible") forceUnlockScrollIfStuck("visible");
    };
    const onPageShow = (e: PageTransitionEvent) => {
      dlog("pageshow", { persisted: e.persisted });
      forceUnlockScrollIfStuck("pageshow");
    };
    const onPageHide = (e: PageTransitionEvent) => dlog("pagehide", { persisted: e.persisted });
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("pagehide", onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAndroidRef = useRef(false);
  // #449: 최신 크롬(안드로이드)의 예측 뒤로가기 제스처는 같은 문서 안의 history entry를
  // popstate 없이 여러 개 건너뛰어 버림(해시로 entry를 구분해도 동일하게 재현됨) — history
  // depth를 쌓는 방식 자체가 근본적으로 안 맞아서, Navigation API가 있으면 아래
  // "Navigation API 기반 가드" effect로 완전히 대체한다. 없는 구형 브라우저에서만
  // 이 아래 sentinel 방식(기존 로직 그대로)을 사용.
  const useNavApiRef = useRef(false);
  useEffect(() => {
    isAndroidRef.current = /Android/i.test(navigator.userAgent || "");
    useNavApiRef.current = isAndroidRef.current && hasNavigationApi();
    dlog("detect", { isAndroid: isAndroidRef.current, useNavApi: useNavApiRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigation API 기반 가드 (#449) — window.navigation 이 있으면 이 effect가 전담.
  // #449 (3차): 히스토리 버퍼를 아예 없앴더니, 뒤로가기 목적지가 항상 "완전히 다른 문서"
  // (교차 오리진/특수 페이지)가 돼버려 event.preventDefault()가 씹혔다(교차 문서 이동은
  // 취소 불가능한 경우가 있음) — 모달 닫기도 토스트도 전부 무시되고 그냥 종료됨.
  // 같은 문서(same-document) entry가 현재 위치 바로 뒤에 최소 1개는 있어야 preventDefault가
  // 먹힌다.
  // 2026.07.17: 여기서 하던 "마운트 시 무조건 1회 push"는 아래의 홈("/") 도착 시마다
  // 재앵커링하는 effect와 기능이 겹쳐 가짜 history push 지점만 늘리고 있었으므로 제거.
  // 홈에서 마운트되는 일반적인 경우엔 재앵커링 effect가 같은 마운트 사이클에 push를
  // 대신 심어주고, 홈이 아닌 곳(배너 딥링크 등)에서 마운트되는 경우엔 애초에 이 시점에
  // 종료가드가 필요 없음(홈이 아니면 가드 로직 자체가 통과시키도록 이미 수정됨) — 리스너
  // 부착만 이 effect가 담당.
  useEffect(() => {
    // #449 재조사(2026-07-14): navApi_listenerAttached 가 실기기 93건 전부에서 단 한 번도
    // 로깅되지 않은 것을 발견 — 이 effect 진입 자체는 되는지, 어느 지점에서 끊기는지 정밀 계측.
    dlog("navApi_effectEntered", { isAndroid: isAndroidRef.current, useNavApi: useNavApiRef.current });
    if (!isAndroidRef.current || !useNavApiRef.current) { dlog("navApi_bail_notApplicable"); return; }
    const nav = window.navigation;
    if (!nav) { dlog("navApi_bail_noWindowNavigation"); return; }

    console.log("[BackGuard:navApi] listener attached");
    dlog("navApi_listenerAttached", { historyLength: window.history.length });

    const onNavigate = (event: NavigateEvent) => {
      // eslint-disable-next-line no-console
      console.log("[BackGuard:navApi] RAW navigate event", {
        navigationType: event.navigationType,
        userInitiated: event.userInitiated,
        canIntercept: event.canIntercept,
        cancelable: event.cancelable,
        destUrl: event.destination?.url,
      });
      dlog("navApi_rawEvent", {
        navigationType: event.navigationType,
        userInitiated: event.userInitiated,
        cancelable: event.cancelable,
        sameDocument: event.destination?.sameDocument,
        destUrl: event.destination?.url,
        // 인트로 영상 스킵(클릭) vs 자연종료(onEnded, 사용자 제스처 없음) 시 뒤로가기 토스트
        // 유무가 갈린다는 신고 — user activation(브라우저의 "최근 사용자 상호작용" 상태) 차이
        // 가설 검증용.
        uaHasBeenActive: (navigator as any).userActivation?.hasBeenActive,
        uaIsActive: (navigator as any).userActivation?.isActive,
      });
      if (event.navigationType !== "traverse" || !event.userInitiated) return;
      let destPath = "/";
      try { destPath = new URL(event.destination.url).pathname; } catch { /* */ }
      const curPath = window.location.pathname;
      // eslint-disable-next-line no-console
      console.log("[BackGuard:navApi] traverse", {
        sameDocument: event.destination.sameDocument,
        cancelable: event.cancelable,
        destPath, curPath,
        exitArmed: exitArmedRef.current,
      });
      dlog("navApi_traverse", {
        sameDocument: event.destination.sameDocument,
        cancelable: event.cancelable,
        destPath, curPath,
        exitArmed: exitArmedRef.current,
      });

      // #449 재조사: traverse 로그까진 찍히는데 그 다음 분기(closedModal/armToast/exit) 로그가
      // 통째로 안 찍히는 사례 발견 — 이 블록 안 어딘가(closeTopModal/preventDefault 등)에서
      // 조용히 예외가 나서 이후 코드가 통째로 스킵될 가능성 대비, 전체를 try/catch로 감싸고
      // 캐치되면 반드시 전용 태그로 남기도록.
      try {
        if (closeTopModal()) {
          console.log("[BackGuard:navApi] branch: closedModal");
          dlog("navApi_closedModal");
          if (event.cancelable) event.preventDefault();
          return;
        }
        // 앱 내 다른 라우트(예: /login → /)로의 정상 전환 — 그대로 통과
        if (event.destination.sameDocument && destPath !== curPath) {
          console.log("[BackGuard:navApi] branch: inAppRoute");
          dlog("navApi_inAppRoute");
          return;
        }
        // 2026.07.17 재분석: 가드 entry가 홈("/")이 아닌 다른 경로(배너 공유링크 진입,
        // 백그라운드 종료 후 재개 등으로 BackGuard가 홈이 아닌 곳에서 최초 마운트된 경우)에
        // 고정돼 destPath===curPath 가 홈이 아닌 페이지에서도 성립하던 것이 "종료확인 토스트가
        // 불규칙하게 뜨거나 안 뜨는" 근본원인이었음 — 홈이 아니면 종료가드 대상이 아니므로
        // 그대로 통과(가로채지 않음).
        if (curPath !== "/") {
          console.log("[BackGuard:navApi] branch: nonHomeSamePath (passthrough)");
          dlog("navApi_nonHomeSamePath", { curPath });
          return;
        }
        if (!exitArmedRef.current) {
          console.log("[BackGuard:navApi] branch: ARM+TOAST");
          dlog("navApi_armToast", { cancelable: event.cancelable });
          if (event.cancelable) event.preventDefault();
          exitArmedRef.current = true;
          showToast(tRef.current("common.exit_confirm", { defaultValue: "한 번 더 누르면 종료됩니다." }), tapConfirmExit);
          if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
          armedTimerRef.current = window.setTimeout(() => { exitArmedRef.current = false; }, EXIT_ARM_WINDOW_MS);
        } else {
          console.log("[BackGuard:navApi] branch: EXIT!!");
          dlog("navApi_exit", { cancelable: event.cancelable });
          // #449 재조사(2026-07-15): 기존엔 preventDefault() 로 실제 종료 네비게이션을 막고
          // window.close()+navigate(-2) 로 대체하려 했는데, window.close() 는 스크립트로
          // 열지 않은 탭에서는 브라우저가 항상 무시하는 보편적 보안 정책이라 실제로는 아무
          // 효과가 없었음 — 사용자가 두 번째로 눌러도 앱이 종료되지 않고 아무 반응 없는
          // 버그의 직접 원인. preventDefault 자체를 하지 않고 브라우저의 실제 뒤로가기를
          // 그대로 통과시켜서, 남은 히스토리가 있으면 정상적으로 벗어나고 없으면 안드로이드
          // 시스템 자체의 기본 동작(앱 최소화/종료)에 맡긴다.
          if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
        }
      } catch (e) {
        dlog("navApi_branchThrew", { err: String(e) });
      }
    };
    try {
      nav.addEventListener("navigate", onNavigate);
      dlog("navApi_addEventListenerDone");
    } catch (e) {
      dlog("navApi_addEventListenerThrew", { err: String(e) });
    }
    return () => {
      dlog("navApi_effectCleanup");
      nav.removeEventListener("navigate", onNavigate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026.07.17 재분석: 위 마운트-시점 1회 푸시는 앱이 홈("/")이 아닌 다른 경로(배너 공유링크
  // 진입, 백그라운드 종료 후 재개 등)에서 최초 마운트되면 종료가드 기준점이 그 페이지에
  // 고정돼버려 홈에서의 뒤로가기가 제대로 감지되지 않는 원인이 됨. 라우터로 실제 홈에
  // 도달할 때마다(진입 경로와 무관하게) 그 시점 기준으로 가드 entry를 다시 심어서, 기준점이
  // 항상 최신 "홈 도착 시점"을 따라가도록 한다. 이미 홈에 있는 entry가 있어도 중복 push는
  // 무해함(위 마운트 sentinel도 동일하게 여유분을 더 심는 방식).
  useEffect(() => {
    if (!isAndroidRef.current || !useNavApiRef.current) return;
    if (location.pathname !== "/") return;
    navigate(location.pathname + location.search + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
    dlog("navApi_homeReanchor", { historyLength: window.history.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 최초 진입 시 sentinel 2개 push (같은 경로 + 새 state — react-router 자체 API).
  // 1개만 심으면 완전히 새 세션(탭 히스토리에 이 페이지 진입 기록 1개뿐)에서 첫 뒤로가기가
  // sentinel 을 지나 실제 페이지 이탈까지 건너뛰는 현상이 있어(#449), 여유분 1개 더 확보.
  // Navigation API 사용 시(useNavApiRef)엔 이 sentinel 자체가 불필요.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current || !isAndroidRef.current || useNavApiRef.current) return;
    mountedRef.current = true;
    const path = location.pathname + location.search;
    navigate(path + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
    navigate(path + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
    dlog("sentinel_fallback_pushed", { historyLength: window.history.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
      if (toastElRef.current) toastElRef.current.remove();
    };
  }, []);

  // react-router 는 페이지 최초 로드 시 navigationType 기본값을 "POP"(뒤로가기와 동일)으로
  // 준다 — 이 최초 1회를 실제 뒤로가기로 오인하면 앱 시작하자마자 토스트가 뜨고 종료가드가
  // 미리 켜져버려, 정작 첫 진짜 뒤로가기에서 곧장 종료되는 버그가 생김 (#449 재현 원인).
  // 최초 effect 실행은 반드시 건너뛴다.
  const skippedInitialRef = useRef(false);
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[BackGuard] effect fired", {
      isAndroid: isAndroidRef.current,
      skippedInitial: skippedInitialRef.current,
      navigationType,
      pathname: location.pathname,
      state: location.state,
      historyLength: window.history.length,
      exitArmed: exitArmedRef.current,
    });
    if (!isAndroidRef.current || useNavApiRef.current) { console.log("[BackGuard] bail: not android or using navApi"); return; }
    if (!skippedInitialRef.current) {
      skippedInitialRef.current = true;
      prevPathRef.current = location.pathname;
      console.log("[BackGuard] bail: skip initial");
      dlog("fallback_skipInitial");
      return;
    }
    if (navigationType !== "POP") {
      // 우리 자신의 navigate() 호출(PUSH) — 가드 로직 대상 아님. path 추적만 갱신.
      prevPathRef.current = location.pathname;
      console.log("[BackGuard] bail: not POP", navigationType);
      return;
    }
    dlog("fallback_pop", { pathname: location.pathname, historyLength: window.history.length, exitArmed: exitArmedRef.current });
    // #449: 모달이 UI 버튼 등으로 닫히며 스스로 정리한 POP — 종료 가드 로직 전체 무시
    if (consumeSuppressFlag()) { console.log("[BackGuard] bail: suppressed"); dlog("fallback_suppressed"); return; }

    // 1) 모달 열려있으면 모달 닫기 우선 + 가드 유지
    if (closeTopModal()) {
      console.log("[BackGuard] branch: closedModal");
      dlog("fallback_closedModal");
      prevPathRef.current = location.pathname;
      navigate(location.pathname + location.search + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
      return;
    }

    const newPath = location.pathname;
    const transitioned = prevPathRef.current !== newPath;
    // 2) 페이지가 실제 전환됐으면 (라우터 백) — 가드만 재푸시 후 라우터 처리에 맡김
    if (transitioned) {
      console.log("[BackGuard] branch: transitioned");
      prevPathRef.current = newPath;
      navigate(newPath + location.search + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
      return;
    }
    // 3) 비-홈 페이지에서 뒤로가기 = 라우터가 추가로 처리할 entry 없음 → 가드만
    if (newPath !== "/") {
      console.log("[BackGuard] branch: nonHomePage");
      navigate(newPath + location.search + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
      return;
    }
    // 4) 홈에서 뒤로가기 — 가드 발동
    if (!exitArmedRef.current) {
      console.log("[BackGuard] branch: ARM+TOAST");
      dlog("fallback_armToast", { historyLength: window.history.length });
      exitArmedRef.current = true;
      showToast(tRef.current("common.exit_confirm", { defaultValue: "한 번 더 누르면 종료됩니다." }), tapConfirmExit);
      navigate(newPath + location.search + nextGuardHash(), { state: { [EXIT_SENTINEL]: true }, replace: false });
      if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
      armedTimerRef.current = window.setTimeout(() => { exitArmedRef.current = false; }, EXIT_ARM_WINDOW_MS);
    } else {
      console.log("[BackGuard] branch: EXIT!!");
      dlog("fallback_exit", { historyLength: window.history.length });
      if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
      try { window.close(); } catch { /* */ }
      try { navigate(-2); } catch { /* */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigationType]);

  return null;
}
